import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { MapPin, Star, ArrowRight, Check, MessageCircle } from 'lucide-react';
import MobileStickyBar from '../components/common/MobileStickyBar';
import ContactModal from '../components/common/ContactModal';

/**
 * Generic SEO Local Page — Show Événement
 * Used for: anniversaire-enfant-figeac, mariage-lot, etc.
 */
// All 15 SEO local pages for Show Événement — Figeac, Lot (46), Occitanie
const SEO_PAGES_CONFIG = {
  'anniversaire-enfant-figeac': {
    title: 'Anniversaire enfant Figeac',
    headline: 'Location animations anniversaire enfant à Figeac',
    description: 'Châteaux gonflables, barbe à papa, popcorn, déco ballon… Créez une fête inoubliable pour votre enfant avec Show Événement, spécialiste événementiel basé à Figeac.',
    seo_title: 'Anniversaire enfant Figeac — Location jeux & animations | Show Événement',
    seo_desc: 'Location château gonflable, barbe à papa, animations enfants à Figeac. Show Événement livre et installe dans tout le Lot (46). Devis gratuit.',
    localite: 'Figeac',
    zone: 'Lot (46)',
    univers: 'anniversaire_enfant',
    products_highlight: ['Château gonflable licorne', 'Machine barbe à papa', 'Mini golf 9 trous', 'Bar à bonbons'],
    benefits: [
      'Livraison et installation à domicile ou en salle',
      'Matériel nettoyé et vérifié avant chaque location',
      'Soufflerie silencieuse incluse pour les gonflables',
      'Assistance téléphonique pendant votre événement'
    ],
    faq: [
      { q: 'Livrez-vous à Figeac ?', a: 'Oui ! Figeac est notre zone principale. Nous livrons dans toute la ville et ses alentours sans frais supplémentaires jusqu’à 20km.' },
      { q: 'Quel âge pour les châteaux gonflables ?', a: 'Nos châteaux gonflables sont adaptés aux enfants de 3 à 12 ans. Nous avons aussi des structures pour les plus grands.' },
      { q: 'Faut-il un espace extérieur ?', a: 'Nos gonflables s’installent en intérieur comme en extérieur (jardin, salle des fêtes, gymnase). Surface minimum : 8x8m.' },
      { q: 'Quel est le tarif moyen ?', a: 'Un anniversaire enfant complet (château + barbe à papa) commence à partir de 250€ tout compris à Figeac.' }
    ]
  },
  'mariage-lot': {
    title: 'Mariage dans le Lot',
    headline: 'Animations et prestations mariage dans le Lot (46)',
    description: 'Photobooth, bars gourmands, coin enfants, décoration sur-mesure… Show Événement crée des mariages inoubliables dans un rayon de 1h30 autour de Figeac.',
    seo_title: 'Mariage Lot (46) — Location photobooth & animations | Show Événement',
    seo_desc: 'Photobooth, bars gourmands, coin enfants, décoration mariage dans le Lot. Livraison Figeac, Cahors, Saint-Céré. Devis gratuit en ligne.',
    localite: 'Lot (46)',
    zone: 'Lot, Aveyron, Cantal',
    univers: 'mariage',
    products_highlight: ['Cabine photobooth premium', 'Bar à bonbons complet', 'Coin enfants mariage', 'Arche décorative ballons'],
    benefits: [
      'Disponibilité week-ends et jours fériés',
      'Installation la veille possible',
      'Conseiller dédié pour votre journée',
      'Matériel premium de qualité professionnelle'
    ],
    faq: [
      { q: 'Intervenez-vous dans tout le Lot pour les mariages ?', a: 'Nous intervenons dans un rayon de 1h30 autour de Figeac : Lot (46), Aveyron, Cantal, Corrèze — Figeac, Cahors, Saint-Céré, Gourdon, Souillac et bien d’autres communes.' },
      { q: 'Peut-on réserver le photobooth pour un mariage ?', a: 'Absolument ! Notre cabine photobooth est très demandée pour les mariages. Nous recommandons de réserver 6 semaines à l’avance.' },
      { q: 'Restez-vous sur place pendant le mariage ?', a: "Oui ! Nous proposons une prestation de présence, surveillance et animation. Nous sommes également joignables 24h/24 durant l'événement en cas de besoin ou panne." },
      { q: 'Proposez-vous des forfaits mariage complets ?', a: 'Oui, nous pouvons créer un forfait personnalisé avec photobooth + animations gourmandes + coin enfants + décoration.' }
    ]
  },
  'jeux-gonflables-figeac': {
    title: 'Jeux gonflables Figeac',
    headline: 'Location jeux gonflables à Figeac et dans le Lot',
    description: 'Châteaux gonflables, parcours d’obstacles, structures géantes… Show Événement, votre spécialiste location jeux gonflables à Figeac depuis plusieurs années.',
    seo_title: 'Location jeux gonflables Figeac — Château, parcours | Show Événement',
    seo_desc: 'Location château gonflable, parcours d’obstacles à Figeac et dans le Lot. Livraison, installation, soufflerie incluses. Réservez en ligne.',
    localite: 'Figeac',
    zone: 'Lot (46)',
    univers: 'anniversaire_enfant',
    products_highlight: ['Château gonflable licorne', 'Parcours gonflable aventure', 'Mini golf 9 trous'],
    benefits: [
      'Soufflerie silencieuse incluse',
      'Installation en 30 minutes',
      'Matériel certifié et assuré',
      'Gonflables adaptés intérieur et extérieur'
    ],
    faq: [
      { q: 'Livrez-vous les jeux gonflables à Figeac ?', a: 'Oui ! Figeac est notre base. Livraison gratuite jusqu’à 20km, frais kilométriques au-delà.' },
      { q: 'Combien de temps pour installer un château gonflable ?', a: 'En moyenne 20 à 30 minutes. Notre équipe s’occupe de tout : gonflage, fixations et sécurité.' },
      { q: 'Que se passe-t-il en cas de vent fort ?', a: 'Pour la sécurité, nous déconseillons l’utilisation des gonflables par vent de plus de 50km/h. Nous resterons en contact la veille.' }
    ]
  },
  'animation-entreprise-lot': {
    title: 'Animation entreprise Lot',
    headline: 'Animations et cocktails d’entreprise dans le Lot',
    description: 'Team building, cocktails, animations CE… Show Événement organise vos événements d’entreprise dans un rayon de 1h30 autour de Figeac avec des tarifs CE avantageux.',
    seo_title: 'Animation entreprise Lot (46) — Cocktail, CE, team building | Show Événement',
    seo_desc: 'Animations et cocktails d’entreprise dans le Lot. Tarifs CE avantageux. Jeux, animations gourmandes, photobooth. Figeac, Cahors, Lot (46).',
    localite: 'Lot',
    zone: 'Lot, Aveyron, Correze',
    univers: 'entreprise',
    products_highlight: ['Pack animation cocktail', 'Cabine photobooth', 'Mini golf 9 trous', 'Blind test musical'],
    benefits: [
      'Tarifs CE et association jusqu’à 20% moins chers',
      'Facturation professionnelle',
      'Disponibilité jours de semaine',
      'Animation clé en main'
    ],
    faq: [
      { q: 'Proposez-vous des tarifs spéciaux pour les CE ?', a: 'Oui ! Les comités d’entreprise bénéficient de tarifs préférentiels allant jusqu’à 20% de remise.' },
      { q: 'Pouvez-vous intervenir en semaine ?', a: 'Absolument. Nous sommes disponibles du lundi au dimanche pour les événements d’entreprise.' },
      { q: 'Fournissez-vous des factures professionnelles ?', a: 'Oui, toutes nos prestations donnent lieu à une facture professionnelle avec TVA.' }
    ]
  },

  // ===== 10 NOUVELLES PAGES SEO PHASE 5 =====
  
  'anniversaire-adulte-figeac': {
    title: 'Anniversaire adulte Figeac',
    headline: 'Location animations anniversaire adulte à Figeac',
    description: 'Photobooth, blind test, brasero, jeux en bois… Créez un anniversaire adulte inoubliable à Figeac avec Show Événement, votre spécialiste événementiel du Lot.',
    seo_title: 'Anniversaire adulte Figeac — Photobooth, blind test, animations | Show Événement',
    seo_desc: 'Location animations anniversaire adulte à Figeac. Photobooth, blind test, jeux bois géants. Livraison et installation Lot (46). Devis gratuit.',
    localite: 'Figeac',
    zone: 'Lot (46), Aveyron',
    univers: 'anniversaire_adulte',
    products_highlight: ['Cabine photobooth premium', 'Blind test musical', 'Pack jeux en bois', 'Brasero garden party'],
    benefits: [
      'Animations adaptées adultes 18-99 ans',
      'Photobooth impression instantanée',
      'Livraison et installation à Figeac et alentours',
      'Devis gratuit en 2 minutes'
    ],
    faq: [
      { q: 'Quelles animations pour un anniversaire adulte à Figeac ?', a: 'Nous proposons le photobooth, blind test, jeux en bois géants, brasero et bars gourmands. Parfait pour 20 à 150 personnes.' },
      { q: 'Livrez-vous en soirée pour un anniversaire ?', a: 'Oui ! Nous livrons à toute heure, de 8h à 22h, selon vos contraintes.' },
      { q: "Peut-on combiner plusieurs animations ?", a: "Absolument. Utilisez notre configurateur pour créer votre pack sur-mesure avec un tarif optimisé." }
    ]
  },
  'anniversaire-enfant-capdenac': {
    title: 'Anniversaire enfant Capdenac',
    headline: 'Location animations anniversaire enfant à Capdenac-Gare',
    description: "Châteaux gonflables, barbe à papa, mini golf… Fêtez l'anniversaire de votre enfant à Capdenac avec Show Événement, votre prestataire événementiel local basé à Figeac.",
    seo_title: 'Anniversaire enfant Capdenac-Gare — Château gonflable, animations | Show Événement',
    seo_desc: 'Location château gonflable et animations anniversaire enfant à Capdenac-Gare. Show Événement livre depuis Figeac. Devis immédiat.',
    localite: 'Capdenac-Gare',
    zone: 'Capdenac, Figeac, Lot (46)',
    univers: 'anniversaire_enfant',
    products_highlight: ['Château gonflable licorne', 'Machine barbe à papa', 'Mini golf 9 trous', 'Bar à bonbons'],
    benefits: [
      'Livraison rapide depuis Figeac (15 min)',
      'Matériel adapté enfants 3-12 ans',
      'Installation complète par notre équipe',
      'Tarifs accessibles dès 150€'
    ],
    faq: [
      { q: "Show Événement livre-t-il à Capdenac ?", a: "Oui ! Capdenac est à 15 minutes de notre base à Figeac. Livraison sans supplément kilométrique." },
      { q: 'Faut-il un grand espace pour un château gonflable ?', a: 'Minimum 8x8 mètres. Nous pouvons installer en intérieur (gymnase, salle) comme en extérieur (jardin, cour).' }
    ]
  },
  'location-photobooth-figeac': {
    title: 'Location photobooth Figeac',
    headline: 'Location photobooth à Figeac — Impression instantanée',
    description: "Cabine photobooth fermée ou borne ouverte avec impression instantanée pour vos mariages, anniversaires et événements d'entreprise à Figeac et dans le Lot.",
    seo_title: 'Location photobooth Figeac — Cabine photo mariage, anniversaire | Show Événement',
    seo_desc: 'Location photobooth et borne selfie à Figeac. Impression photo HD en 10 secondes, fond personnalisable, accessoires inclus. Lot (46).',
    localite: 'Figeac',
    zone: 'Lot (46), Aveyron, Cantal',
    univers: 'mariage',
    products_highlight: ['Cabine photobooth premium', 'Borne photo selfie', 'Kit déco table mariage'],
    benefits: [
      'Impression HD en 10 secondes',
      'Cadre personnalisé avec votre texte/logo',
      'Accessoires et réquisits fournis',
      'Partage numérique QR code inclus'
    ],
    faq: [
      { q: "Quel est le tarif de location d'un photobooth à Figeac ?", a: "La cabine photobooth est disponible à partir de 320€ pour un particulier, livraison et installation incluses à Figeac." },
      { q: "Peut-on personnaliser les photos avec le nom du mariage ?", a: "Oui ! Cadre personnalisé avec prénoms, date et design. Envoyez vos éléments 5 jours avant l'événement." },
      { q: 'Combien de photos peut-on faire ?', a: 'Pas de limite ! En moyenne 200 tirages pour un mariage de 6 heures.' }
    ]
  },
  'location-jeux-gonflables-decazeville': {
    title: 'Location jeux gonflables Decazeville',
    headline: 'Location jeux gonflables à Decazeville et alentours',
    description: "Châteaux gonflables, parcours d'obstacles, structures géantes pour anniversaires enfants et événements à Decazeville. Show Événement livre depuis Figeac dans tout le nord Aveyron.",
    seo_title: 'Location jeux gonflables Decazeville — Château, parcours | Show Événement',
    seo_desc: 'Location château gonflable et jeux gonflables à Decazeville. Livraison depuis Figeac. Anniversaires, kermesses. Aveyron, Lot. Devis gratuit.',
    localite: 'Decazeville',
    zone: 'Decazeville, Figeac, Aveyron (12)',
    univers: 'anniversaire_enfant',
    products_highlight: ['Château gonflable licorne', 'Parcours gonflable aventure', 'Mini golf 9 trous'],
    benefits: [
      'Livraison à Decazeville depuis Figeac',
      'Soufflerie silencieuse incluse',
      'Installation en moins de 45 minutes',
      'Matériel certifié sécurité enfants'
    ],
    faq: [
      { q: 'Livrez-vous à Decazeville ?', a: 'Oui ! Decazeville est à 30 minutes de Figeac. Nous livrons dans tout le nord Aveyron avec un forfait kilométrique raisonnable.' },
      { q: 'Pour quels événements proposez-vous des gonflables à Decazeville ?', a: 'Anniversaires enfants, kermesses scolaires, fêtes de village, animations associatives. Tarifs association disponibles.' }
    ]
  },
  'animation-mariage-lot': {
    title: 'Animation mariage Lot',
    headline: 'Animations et prestations mariage dans tout le Lot (46)',
    description: 'Photobooth, bars gourmands, coin enfants, blind test, décoration sur-mesure… Show Événement anime vos mariages dans un rayon de 1h30 autour de Figeac avec des prestations premium.',
    seo_title: 'Animation mariage Lot (46) — Photobooth, bar gourmand, décoration | Show Événement',
    seo_desc: 'Animations mariage premium dans le Lot : photobooth, bar à bonbons, coin enfants. Figeac, Cahors, Saint-Céré, Gourdon. Devis gratuit.',
    localite: 'Lot (46)',
    zone: 'Lot, Aveyron, Cantal, Corrèze',
    univers: 'mariage',
    products_highlight: ['Cabine photobooth premium', 'Bar à bonbons complet', 'Coin enfants mariage', 'Arche décorative ballons'],
    benefits: [
      'Spécialiste animations mariage depuis plusieurs années',
      'Installation la veille possible',
      'Conseiller dédié pour votre Jour J',
      'Prestation clé en main — rayon 1h30 autour de Figeac'
    ],
    faq: [
      { q: 'Vous intervenez dans quelle zone pour les mariages ?', a: "Tout le Lot (46) et département voisins : Aveyron (12), Cantal (15), Corrèze (19), Tarn (81). Nous intervenons dans un rayon de 1h30 autour de Figeac." },
      { q: 'Quelle est votre prestation mariage la plus populaire ?', a: 'Le photobooth est incontournable, souvent combiné avec un bar à bonbons et un coin enfants. Un trio parfait pour satisfaire tous vos invités.' }
    ]
  },
  'animation-entreprise-figeac': {
    title: 'Animation entreprise Figeac',
    headline: "Animations d'entreprise et cocktails professionnels à Figeac",
    description: "Team building, cocktails d'entreprise, animations CE et animations commerciales… Show Événement organise vos événements professionnels à Figeac avec des tarifs CE avantageux.",
    seo_title: 'Animation entreprise Figeac — Cocktail, CE, team building | Show Événement',
    seo_desc: "Animations d'entreprise à Figeac : cocktail, CE, team building, animations commerciales. Tarifs CE avantageux. Lot (46). Devis immédiat.",
    localite: 'Figeac',
    zone: 'Lot (46)',
    univers: 'entreprise',
    products_highlight: ['Pack animation cocktail', 'Cabine photobooth premium', 'Blind test musical animé', 'Mini golf 9 trous'],
    benefits: [
      "Tarifs CE et association jusqu'à 20% de remise",
      'Facturation pro avec TVA',
      'Disponible en semaine et week-end',
      "Animation clé en main, 0 logistique pour vous"
    ],
    faq: [
      { q: 'Organisez-vous des animations pour les entreprises de Figeac ?', a: 'Oui ! Figeac est notre bassin principal. Nous intervenons pour les entreprises locales, les CE et associations du Lot.' },
      { q: "Quels événements professionnels animez-vous ?", a: "Cocktails d'inauguration, afterworks, séminaires, journées de team building, animations commerciales en magasin, fêtes de fin d'année." }
    ]
  },
  'bar-a-bonbons-lot': {
    title: 'Bar à bonbons Lot',
    headline: 'Location bar à bonbons dans le Lot (46)',
    description: "Bar à bonbons sur-mesure pour mariages, anniversaires, baptêmes et événements d'entreprise dans un rayon de 1h30 autour de Figeac. Show Événement livre, installe et reprend votre candy bar.",
    seo_title: 'Location bar à bonbons Lot (46) — Candy bar mariage, anniversaire | Show Événement',
    seo_desc: 'Location bar à bonbons dans le Lot. Candy bar mariage, anniversaire, baptême. Montage inclus. Figeac, Cahors, Gourdon. Devis gratuit.',
    localite: 'Lot (46)',
    zone: 'Lot, Aveyron',
    univers: 'anniversaire_enfant',
    products_highlight: ['Bar à bonbons complet', 'Table candy bar décorée', 'Machine barbe à papa', 'Bar à crêpes'],
    benefits: [
      'Bocaux, bonbons et contenants fournis',
      'Personnalisation couleurs et thème',
      'Installation clé en main',
      'Adapté 30 à 200 personnes'
    ],
    faq: [
      { q: 'Fournissez-vous les bonbons avec le bar ?', a: 'Oui ! Notre bar à bonbons inclut les bocaux, les bonbons assortis (sucreries, dragées, réglisses) et les contenants pour les invités.' },
      { q: 'Peut-on personnaliser le candy bar aux couleurs du mariage ?', a: 'Absolument. Nous sélectionnons les bonbons selon votre palette de couleurs. Indiquez vos teintes lors de la commande.' }
    ]
  },
  'location-materiel-evenementiel-figeac': {
    title: 'Location matériel événementiel Figeac',
    headline: "Location matériel événementiel à Figeac — Toutes animations",
    description: 'Tout pour votre événement en un seul endroit : jeux gonflables, photobooth, animations gourmandes, décoration, jeux en bois. Show Événement, votre loueur événementiel à Figeac.',
    seo_title: 'Location matériel événementiel Figeac — Jeux, photobooth, animation | Show Événement',
    seo_desc: 'Location matériel événementiel à Figeac. Château gonflable, photobooth, barbe à papa, jeux bois. Livraison et installation Lot (46).',
    localite: 'Figeac',
    zone: 'Lot (46)',
    univers: 'anniversaire_enfant',
    products_highlight: ['Château gonflable licorne', 'Cabine photobooth premium', 'Machine barbe à papa', 'Pack jeux en bois'],
    benefits: [
      'Plus de 20 animations disponibles',
      'Location courte durée (demi-journée à week-end)',
      'Livraison et installation comprises',
      'Un seul interlocuteur pour tout votre événement'
    ],
    faq: [
      { q: "Puis-je louer plusieurs animations en même temps ?", a: "Bien sûr ! Nous gérons des prestations multi-animations. Notre configurateur vous permet de composer votre événement et de voir le tarif total." },
      { q: "Quel est le délai minimum pour réserver ?", a: "Nous recommandons 2 semaines pour les week-ends. Mais des disponibilités de dernière minute existent, appelez-nous!" }
    ]
  },
  'animation-commerciale-lot': {
    title: 'Animation commerciale Lot',
    headline: 'Animation commerciale et événementielle dans le Lot',
    description: "Animations de rue, animations en magasin, attractivité commerciale… Show Événement propose des solutions d'animation pour booster la fréquentation de votre commerce dans le Lot.",
    seo_title: 'Animation commerciale Lot (46) — Événementiel commerce, magasin | Show Événement',
    seo_desc: 'Animation commerciale dans le Lot : jeux, attractions, animations gourmandes pour commerces et zones commerciales. Figeac, Cahors. Devis.',
    localite: 'Lot (46)',
    zone: 'Lot (46)',
    univers: 'entreprise',
    products_highlight: ['Machine barbe à papa', 'Mini golf 9 trous', 'Borne photo selfie', 'Bar à bonbons'],
    benefits: [
      'Animations attractives pour tous les âges',
      'Mise en place rapide en milieu commercial',
      'Tarifs professionnels et devis sur-mesure',
      'Expérience animations commerciales en zone Lot'
    ],
    faq: [
      { q: 'Pouvez-vous animer un événement commercial en extérieur ?', a: 'Oui ! Nos animations sont adaptées aux espaces extérieurs : parking de centre commercial, marché, zone piétonne.' },
      { q: 'Quelles animations fonctionnent le mieux en contexte commercial ?', a: 'La barbe à papa et le popcorn sont des valeurs sûres (odeur attirante !). Le mini golf crée du temps de présence. La borne selfie génère du partage sur les réseaux.' }
    ]
  },
  'location-mini-golf-evenementiel': {
    title: 'Location mini golf événementiel',
    headline: 'Location mini golf événementiel — Lot et Aveyron',
    description: "Mini golf 9 trous transportable pour anniversaires, mariages, entreprises et animations commerciales. Show Événement livre et installe votre parcours de mini golf dans le Lot et l'Aveyron.",
    seo_title: 'Location mini golf événementiel Lot Aveyron | Show Événement Figeac',
    seo_desc: "Location mini golf 9 trous pour événements dans le Lot et l'Aveyron. Anniversaires, mariages, entreprises. Livraison Figeac. Devis gratuit.",
    localite: 'Lot (46)',
    zone: 'Lot, Aveyron',
    univers: 'jeux',
    products_highlight: ['Mini golf 9 trous', 'Pack jeux en bois géants', 'Parcours gonflable aventure'],
    benefits: [
      'Parcours 9 trous complet (clubs, balles, drapeaux)',
      'Installation en 45 minutes',
      'Adapté enfants et adultes',
      'Idéal intérieur et extérieur'
    ],
    faq: [
      { q: 'Quelle surface faut-il pour un mini golf 9 trous ?', a: "Environ 80m² minimum. Le parcours est modulable selon l'espace disponible (salle des fêtes, jardin, parking)." },
      { q: "Le mini golf convient-il pour les enfants ?", a: "Oui ! À partir de 4 ans accompagnés. Nos clubs sont adaptés aux enfants. L'activité plaît autant aux parents qu'aux enfants." },
      { q: 'Peut-on organiser un tournoi de mini golf ?', a: 'Absolument ! Nous pouvons fournir des feuilles de score et mettre en place un système de points. Parfait pour les team building.' }
    ]
  },

  // ===== NOUVELLES PAGES PAR VILLE =====
  
  'location-evenementielle-cahors': {
    title: 'Location événementielle Cahors',
    headline: 'Location matériel événementiel à Cahors et dans le Lot',
    description: 'Show Événement livre à Cahors et dans un rayon de 1h30 autour de Figeac. Jeux gonflables, photobooth, animations gourmandes, brasero pour vos événements.',
    seo_title: 'Location événementielle Cahors Lot (46) | Show Événement Figeac',
    seo_desc: 'Location animations événementielles à Cahors. Château gonflable, photobooth, animations. Livraison depuis Figeac. Lot (46).',
    localite: 'Cahors',
    zone: 'Lot (46)',
    univers: 'anniversaire_enfant',
    products_highlight: ['Château gonflable licorne', 'Cabine photobooth premium', 'Machine barbe à papa', 'Pack jeux en bois'],
    benefits: ['Livraison à Cahors depuis Figeac', 'Tout le Lot (46) couvert', 'Devis gratuit immédiat', 'Matériel premium vérifié'],
    faq: [
      { q: 'Livrez-vous à Cahors ?', a: 'Oui ! Cahors est dans notre zone de livraison. Tarif selon distance depuis Figeac.' },
      { q: 'Quelles animations proposez-vous à Cahors ?', a: 'Toutes nos animations : gonflables, photobooth, food, brasero, mobilier, son. Devis gratuit.' }
    ]
  },
  'location-evenementielle-saint-cere': {
    title: 'Location événementielle Saint-Céré',
    headline: 'Animations événementielles à Saint-Céré et nord du Lot',
    description: 'Show Événement livre et installe dans tout le nord du Lot, dont Saint-Céré. Jeux gonflables, photobooth, bars gourmands.',
    seo_title: 'Location événementielle Saint-Céré Lot (46) | Show Événement',
    seo_desc: 'Location animations événementielles à Saint-Céré. Gonflables, photobooth, food. Livraison Lot (46). Devis gratuit.',
    localite: 'Saint-Céré',
    zone: 'Nord Lot (46)',
    univers: 'anniversaire_enfant',
    products_highlight: ['Château gonflable licorne', 'Machine barbe à papa', 'Mini golf 9 trous', 'Bar à bonbons'],
    benefits: ['Proche de Figeac (40min)', 'Livraison nord Lot', 'Tarif zone 2 avantageux', 'Toutes familles couvertes'],
    faq: [
      { q: 'Livrez-vous à Saint-Céré ?', a: 'Oui ! Saint-Céré est dans notre zone de livraison étendue (40km de Figeac). Tarif livraison 45€.' },
      { q: 'Proposez-vous des animations pour les CE dans le nord Lot ?', a: 'Absolument ! Nous desservons toutes les entreprises et associations du nord Lot.' }
    ]
  },
  'animation-mariage-aveyron': {
    title: 'Animation mariage Aveyron',
    headline: "Animations mariage dans l'Aveyron et nord Aveyron",
    description: "Show Événement intervient en Aveyron pour les mariages. Photobooth, arche ballons, brasero, décoration sur-mesure. Depuis Figeac, proche du nord Aveyron.",
    seo_title: 'Animation mariage Aveyron (12) — Photobooth, décoration | Show Événement',
    seo_desc: "Animations mariage dans l'Aveyron. Photobooth, arche ballons, brasero. Livraison depuis Figeac. Devis gratuit.",
    localite: 'Aveyron (12)',
    zone: 'Aveyron, Lot',
    univers: 'mariage',
    products_highlight: ['Cabine photobooth premium', 'Arche ballons organiques', 'Brasero convivial XXL', 'Coin enfants mariage'],
    benefits: ['Interventions en Aveyron', 'Proche Decazeville, Villefranche', 'Tarif selon distance', 'Devis gratuit'],
    faq: [
      { q: "Intervenez-vous en Aveyron pour les mariages ?", a: "Oui ! Nous intervenons dans le nord Aveyron (Decazeville, Villefranche-de-Rouergue). Tarif sur devis au-delà." }
    ]
  },
  'location-chapiteau-barnum-lot': {
    title: 'Location barnum et chapiteau Lot',
    headline: 'Location barnum et tente événementielle dans le Lot',
    description: 'Show Événement propose la location de barnums 3×6m pour vos garden parties, mariages et événements extérieurs dans le Lot.',
    seo_title: 'Location barnum tente événementielle Lot (46) | Show Événement Figeac',
    seo_desc: 'Location barnum 3×6m pour événements extérieurs dans le Lot. Garden party, mariage, anniversaire. Livraison Figeac.',
    localite: 'Lot (46)',
    zone: 'Lot, Aveyron',
    univers: 'mariage',
    products_highlight: ['Barnum tente 3×6m', 'Pack tables + chaises 50 pers.', 'Guirlande lumineuse', 'Brasero convivial XXL'],
    benefits: ['Barnum 3×6m aluminium', 'Protection soleil et pluie', 'Tables et chaises disponibles', 'Livraison et montage inclus'],
    faq: [
      { q: 'Quel est le tarif du barnum ?', a: 'Notre barnum 3×6m est disponible à partir de 180€, livraison et installation comprises.' },
      { q: 'Peut-on associer tables et chaises ?', a: 'Oui ! Nous proposons un pack barnum + tables + chaises tout inclus.' }
    ]
  },

  // ===== 6 NOUVELLES PAGES VILLES PHASE 11 =====
  
  'animations-figeac': {
    title: 'Animations Figeac',
    headline: 'Show Événement — Votre spécialiste animations à Figeac',
    description: 'Show Événement est basé à Figeac (46100, Lot). Location jeux gonflables, photobooth, animations gourmandes, brasero, décoration. Livraison gratuite à Figeac.',
    seo_title: 'Animations événementielles Figeac 46100 — Location jeux, photobooth | Show Événement',
    seo_desc: 'Show Événement, votre spécialiste animations à Figeac. Location jeux gonflables, photobooth, barbe à papa. Livraison gratuite Figeac et rayon 20km.',
    localite: 'Figeac',
    zone: 'Figeac, Lot (46)',
    univers: 'anniversaire_enfant',
    products_highlight: ['Château gonflable licorne XL', 'Cabine photobooth premium', 'Machine barbe à papa', 'Brasero convivial XXL'],
    benefits: ['Livraison gratuite à Figeac', 'Base Show Événement à Figeac', 'Installation rapide 30min', 'Matériel premium vérifié'],
    faq: [
      { q: 'Show Événement est-il basé à Figeac ?', a: "Oui ! Nous sommes basés à Figeac (46100). Livraison gratuite à Figeac et dans un rayon de 20 km sans supplément." },
      { q: 'Quels sont vos délais à Figeac ?', a: "Nous pouvons souvent intervenir avec 48H de préavis à Figeac. Pour les week-ends chargés, réservez 2 à 3 semaines à l'avance." }
    ]
  },
  'anniversaire-capdenac-gare': {
    title: 'Anniversaire Capdenac-Gare',
    headline: 'Location animations anniversaire à Capdenac-Gare',
    description: "Show Événement livre vos animations d'anniversaire à Capdenac-Gare (15 min de Figeac). Château gonflable, barbe à papa, bar à bonbons, déco.",
    seo_title: 'Anniversaire Capdenac-Gare — Location animations enfant & adulte | Show Événement',
    seo_desc: 'Location animations anniversaire à Capdenac-Gare. Château gonflable, barbe à papa. Livraison depuis Figeac 15 min. Devis gratuit.',
    localite: 'Capdenac-Gare',
    zone: 'Capdenac, Figeac',
    univers: 'anniversaire_enfant',
    products_highlight: ['Château gonflable licorne XL', 'Machine barbe à papa pro', 'Bar à bonbons complet 3m', 'Mini golf 9 trous'],
    benefits: ['15 minutes de Figeac', 'Livraison rapide Capdenac', 'Toutes animations dispo', 'Tarifs accessibles'],
    faq: [
      { q: 'Livrez-vous à Capdenac-Gare ?', a: "Oui ! Capdenac est à 15 minutes de notre base à Figeac. Livraison sans supplément kilométrique." }
    ]
  },
  'location-materiel-decazeville': {
    title: 'Location matériel Decazeville',
    headline: 'Location matériel événementiel à Decazeville',
    description: 'Show Événement livre à Decazeville et dans le bassin Decazeville-Aubin. Jeux gonflables, animations gourmandes, photobooth pour vos événements.',
    seo_title: 'Location matériel événementiel Decazeville — Gonflables, animations | Show Événement',
    seo_desc: 'Location animations événementielles à Decazeville. Gonflables, animations gourmandes, photobooth. Livraison depuis Figeac.',
    localite: 'Decazeville',
    zone: 'Decazeville, Aveyron (12)',
    univers: 'anniversaire_enfant',
    products_highlight: ['Château gonflable licorne XL', 'Machine barbe à papa pro', 'Mini golf 9 trous', 'Pack jeux en bois géants'],
    benefits: ["Livraison à Decazeville", "Proche du bassin Decazeville", "Toutes familles d'animations", "Devis gratuit"],
    faq: [
      { q: 'Livrez-vous à Decazeville ?', a: "Oui ! Decazeville est à 30 minutes de Figeac. Frais de déplacement zone 2 (45€)." }
    ]
  },
  'animation-villefranche-rouergue': {
    title: 'Animation Villefranche-de-Rouergue',
    headline: 'Location animations événementielles à Villefranche-de-Rouergue',
    description: 'Show Événement intervient à Villefranche-de-Rouergue pour vos événements. Jeux gonflables, photobooth, brasero, animations gourmandes.',
    seo_title: 'Animation Villefranche-de-Rouergue — Location événementielle | Show Événement',
    seo_desc: 'Location animations à Villefranche-de-Rouergue. Gonflables, photobooth, brasero. Livraison Aveyron, Lot. Devis gratuit.',
    localite: 'Villefranche-de-Rouergue',
    zone: 'Aveyron (12)',
    univers: 'anniversaire_adulte',
    products_highlight: ['Château gonflable licorne XL', 'Cabine photobooth premium', 'Brasero convivial XXL', 'Pack jeux en bois géants'],
    benefits: ['Interventions Aveyron', 'Proche de Figeac', 'Tarif sur devis', 'Devis gratuit immédiat'],
    faq: [
      { q: 'Intervenez-vous à Villefranche ?', a: "Oui ! Villefranche est dans notre zone étendue. Tarif de livraison selon distance, devis sur demande." }
    ]
  },
  'location-evenementielle-rodez': {
    title: 'Location événementielle Rodez',
    headline: 'Location matériel événementiel à Rodez et en Aveyron',
    description: 'Show Événement peut intervenir à Rodez sur devis. Photobooth, jeux gonflables, brasero pour vos événements en Aveyron.',
    seo_title: 'Location événementielle Rodez Aveyron — Photobooth, gonflables | Show Événement',
    seo_desc: 'Location animations événementielles à Rodez. Photobooth, gonflables, brasero. Devis sur demande. Show Événement Figeac.',
    localite: 'Rodez',
    zone: 'Aveyron (12)',
    univers: 'anniversaire_adulte',
    products_highlight: ['Cabine photobooth premium', 'Château gonflable licorne XL', 'Brasero convivial XXL', 'Pack jeux en bois géants'],
    benefits: ['Interventions sur devis Rodez', 'Photobooth et animations', 'Professionnel et ponctuel', 'Devis gratuit'],
    faq: [
      { q: 'Livrez-vous à Rodez ?', a: "Rodez est dans notre rayon de 1h30 autour de Figeac. Nous intervenons sur devis, appelez-nous !" }
    ]
  },
  'location-evenementielle-aurillac': {
    title: 'Location événementielle Aurillac',
    headline: 'Location matériel événementiel à Aurillac et dans le Cantal',
    description: 'Show Événement peut intervenir à Aurillac sur devis. Photobooth, animations gonflables, brasero pour vos événements dans le Cantal.',
    seo_title: 'Location événementielle Aurillac Cantal — Photobooth, animations | Show Événement',
    seo_desc: 'Location animations à Aurillac. Photobooth, gonflables. Sur devis. Show Événement Figeac.',
    localite: 'Aurillac',
    zone: 'Cantal (15)',
    univers: 'anniversaire_adulte',
    products_highlight: ['Cabine photobooth premium', 'Château gonflable licorne XL', 'Brasero convivial XXL', 'Pack jeux en bois géants'],
    benefits: ['Sur devis Aurillac', 'Photobooth premium', 'Matériel vérifié', 'Devis gratuit'],
    faq: [
      { q: 'Livrez-vous à Aurillac ?', a: "Aurillac est dans notre rayon de 1h30 autour de Figeac. Nous intervenons sur devis personnalisé, contactez-nous." }
    ]
  },

  'photobooth-mariage-lot': {
    title: 'Photobooth mariage Lot',
    headline: 'Location photobooth pour mariage dans le Lot (46)',
    description: 'Notre cabine photobooth premium avec impression instantanée est la star de tous les mariages dans le Lot. Réservez votre date dès maintenant.',
    seo_title: 'Photobooth mariage Lot (46) — Cabine photo premium | Show Événement',
    seo_desc: 'Location photobooth mariage dans le Lot. Impression instantanée, fond personnalisable, accessoires inclus. Livraison Figeac, Cahors, Saint-Céré.',
    localite: 'Lot (46)',
    zone: 'Lot, Aveyron',
    univers: 'mariage',
    products_highlight: ['Cabine photobooth premium', 'Borne photo selfie', 'Kit déco table mariage'],
    benefits: [
      'Impression en 10 secondes',
      'Fond et cadre personnalisables',
      'Accessoires et réquisits fournis',
      'Partage numérique inclus'
    ],
    faq: [
      { q: 'Combien de photos peut-on prendre avec le photobooth ?', a: 'Il n’y a pas de limite ! L’imprimante est rechargée à chaque besoin. En moyenne 150-200 tirages pour un mariage.' },
      { q: 'Peut-on personnaliser le cadre photo ?', a: 'Oui ! Envoyez-nous votre texte, date et prénoms. Nous créons le cadre graphique gratuitement.' },
      { q: 'Le photobooth fonctionne-t-il en extérieur ?', a: 'Oui, à condition d’avoir un espace couvert ou un abri. Nous conseillons l’installation sous une tonnelle ou dans la salle.' }
    ]
  }
};

export default function SEOLocalPage({ slug }) {
  const config = SEO_PAGES_CONFIG[slug];
  const [contactOpen, setContactOpen] = useState(false);
  
  if (!config) return null;

  return (
    <div style={{ background: '#0D0D0D', minHeight: '100vh' }}>
      <Header />

      {/* Hero */}
      <section
        style={{
          background: `radial-gradient(70% 60% at 20% 10%, rgba(212,175,55,0.12) 0%, rgba(13,13,13,0) 60%),
          radial-gradient(50% 50% at 80% 30%, rgba(197,216,249,0.08) 0%, rgba(13,13,13,0) 55%), #0D0D0D`,
          borderBottom: '1px solid #2A2A2A'
        }}
        className="section-py"
      >
        <div className="container-ose">
          <div className="flex items-center gap-2 mb-4">
            <MapPin size={16} style={{ color: '#D4AF37' }} />
            <span className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>{config.localite}</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#FFFFFF', letterSpacing: '-0.02em', maxWidth: '700px', lineHeight: '1.1' }}>
            {config.headline}
          </h1>
          <p className="text-base mb-8 max-w-2xl" style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.7' }}>
            {config.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/creer-evenement" className="btn-gold px-7 py-3.5 text-base font-semibold rounded-xl flex items-center gap-2">
              Créer mon événement <ArrowRight size={18} />
            </Link>
            <button onClick={() => setContactOpen(true)} className="btn-outline-gold px-7 py-3.5 text-base font-semibold rounded-xl flex items-center gap-2" style={{ cursor: 'pointer', border: 'none' }}>
              <MessageCircle size={16} /> Nous contacter
            </button>          </div>
        </div>
      </section>

      {/* Products highlight */}
      <section className="section-py">
        <div className="container-ose">
          <h2 className="font-display text-2xl font-bold mb-8" style={{ color: '#FFFFFF', letterSpacing: '-0.02em' }}>
            Nos animations phares à {config.localite}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {config.products_highlight.map((p, i) => (
              <div key={`product-${i}`} className="p-4 rounded-xl" style={{ background: '#161616', border: '1px solid #2A2A2A' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: 'rgba(212,175,55,0.12)', color: '#D4AF37' }}>
                  <Star size={18} />
                </div>
                <p className="font-semibold text-sm" style={{ color: '#FFFFFF' }}>{p}</p>
              </div>
            ))}
          </div>

          {/* Benefits */}
          <div className="grid sm:grid-cols-2 gap-3">
            {config.benefits.map((b, i) => (
              <div key={`benefit-${i}`} className="flex items-start gap-3">
                <Check size={16} style={{ color: '#3DDC97', flexShrink: 0, marginTop: '2px' }} />
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Zone badge */}
      <section className="section-py">
        <div className="container-ose">
          <div className="p-6 rounded-2xl flex flex-col sm:flex-row items-center gap-6" style={{ background: '#F5ECD7' }}>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: '#D4AF37' }}>
              <MapPin size={28} color="#0D0D0D" />
            </div>
            <div>
              <h3 className="font-display text-xl font-bold mb-1" style={{ color: '#0D0D0D' }}>Zone d’intervention : {config.zone}</h3>
              <p className="text-sm" style={{ color: 'rgba(13,13,13,0.6)' }}>Show Événement livre et installe dans toute la région. Contactez-nous pour vérifier votre zone.</p>
            </div>
            <Link to="/contact" className="ml-auto flex-shrink-0 btn-gold px-5 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap">
              Vérifier ma zone
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ SEO */}
      <section className="section-py">
        <div className="container-ose">
          <h2 className="font-display text-2xl font-bold mb-6" style={{ color: '#FFFFFF', letterSpacing: '-0.02em' }}>Questions fréquentes</h2>
          <div className="max-w-2xl space-y-3">
            {config.faq.map((f, i) => (
              <div key={`item-${i}`} className="p-4 rounded-xl" style={{ background: '#161616', border: '1px solid #2A2A2A' }}>
                <p className="font-semibold text-sm mb-2" style={{ color: '#D4AF37' }}>{f.q}</p>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.6' }}>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-py">
        <div className="container-ose">
          <div className="text-center p-10 rounded-2xl" style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.2)' }}>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mb-3" style={{ color: '#FFFFFF' }}>
              Vous êtes à {config.localite} ?<br />Créez votre événement maintenant.
            </h2>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.5)' }}>Devis gratuit en 2 minutes. Livraison incluse.</p>
            <Link to="/creer-evenement" className="btn-gold px-8 py-4 text-base font-semibold rounded-xl inline-flex items-center gap-2">
              Créer mon événement <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <MobileStickyBar />
      <Footer />
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} sourcePage={`Page SEO: ${slug}`} />
    </div>
  );
}

export { SEO_PAGES_CONFIG };
